import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { gql, useMutation, useQuery } from '@apollo/client';
import useFeature from '../../utils/hooks/useFeature';
import CalibrationCardView from './CalibrationCardView';
import { InsertBinSetLevelMutation, UpdateBinSetLevelMutation, DeleteBinSetLevelMutation } from './queries';
import './CalibrationCard.scss';
import BinCheckDataPane from './BinCheckDataPane';
import BinSetCalibrationDataPane from './BinSetCalibrationDataPane';
import useUser from '../../utils/hooks/useUser';
import useCalibrationSeriesTail from '../../utils/hooks/useCalibrationSeriesTail';
import withStaffOnlyIdWrapper from '../../utils/withStaffOnlyIdWrapper';
import { convertGramsToLargeUnits } from '../../utils/unitConversion';

const COLOUR_PALETTE = {
  calibratedDataColour: '#26AF5F',
  liveDataColour: '#9be09b',
  originalDataColour: '#585958'
};

const CALIBRATION_CARD_MARKER_DATA_QUERY = gql`
  query CalibrationCard_MarkerData($bin_set_id: uuid!) {
    bin_set(where: { id: { _eq: $bin_set_id }, deleted_at: { _is_null: true } }) {
      id
      farm {
        name
      }
      bins(where: { deleted_at: { _is_null: true } }, order_by: { name: asc }) {
        name
      }
      earliest_bin_set_level: bin_set_levels(
        where: { deleted_at: { _is_null: true } }
        order_by: { valid_at: asc }
        limit: 1
      ) {
        valid_at
      }
      all_bin_set_levels: bin_set_levels(where: { deleted_at: { _is_null: true } }, order_by: { valid_at: asc }) {
        id
        valid_at
        level_in_grams
        purpose
        expected_deviation_in_grams
        calibration_status
      }
    }
  }
`;

const CALIBRATION_CARD_GET_BIN_SET_CALIBRATIONS_QUERY = gql`
  query CalibrationCard_GetBinSetCalibrations($bin_set_id: uuid!) {
    bin_set_calibration(where: { bin_set_id: { _eq: $bin_set_id }, deleted_at: { _is_null: true } }) {
      id
      started_at
      ended_at
      provided_mass_in_grams
      displayed_calibration_step
    }
  }
`;

const CALIBRATION_CARD_CALIBRATION_SERIES_QUERY = gql`
  query CalibrationCard_CalibrationSeries($bin_set_id: uuid!) {
    bin_set_calibration_series(
      where: { bin_set_id: { _eq: $bin_set_id }, deleted_at: { _is_null: true } }
      order_by: { series_start_at: asc }
    ) {
      computed_at
      original_data_series
      calibrated_data_series
      accuracy_details
    }
  }
`;

const REFETCH_QUERIES = [
  'CalibrationCard_MarkerData',
  'CalibrationCard_GetBinSetCalibrations',
  'CalibrationCard_CalibrationSeries',
  'FeedLineCoefficientQuery',
];

function stitchSeriesWithNext(points, nextPoints) {
  if (!points?.length) {
    return [];
  }
  if (!nextPoints?.length) {
    return points;
  }

  const bridgePoint = nextPoints[0];
  const lastPoint = points[points.length - 1];
  if (lastPoint.t === bridgePoint.t && lastPoint.v === bridgePoint.v) {
    return points;
  }

  return [...points, { ...bridgePoint }];
}

function buildCachedSeriesTrace(points, color, name, isMetric) {
  if (!points?.length) {
    return null;
  }

  return {
    type: 'scatter',
    name,
    line: { color, width: 3, dash: 'solid' },
    x: points.map((p) => new Date(p.t * 1000)),
    y: points.map((p) => convertGramsToLargeUnits(isMetric, p.v)),
  };
}

function CalibrationCard({ binSetID = '', feedlines = [], calibrationStyle }) {
  const { user } = useUser();
  const isMetric = user?.isMetric || false;
  const now = useMemo(() => dayjs(), []);
  const { active: hideCoefficientChart } = useFeature('HIDE_COEFFICIENT_CHART');
  const { active: showBinLevelStoreTable } = useFeature('SHOW_BIN_LEVEL_SCORE_TABLE');
  const { active: showStaffElements } = useFeature('SHOW_STAFF_ONLY_ELEMENTS');
  const showCoefficientChart = !hideCoefficientChart;
  const from = dayjs().subtract(6, 'month').startOf('day');
  const to = dayjs().endOf('day');
  const [dateRange, setDateRange] = useState({ to, from });

  const [selectedBinSetLevelId, setSelectedBinSetLevelId] = useState(null);
  const [isCreatingNewBinSetLevel, setIsCreatingNewBinSetLevel] = useState(false);
  const [isCreatingNewBinSetCalibration, setIsCreatingNewBinSetCalibration] = useState(false);
  const [selectedBinSetCalibrationId, setSelectedBinSetCalibrationId] = useState(null);
  const [insertBinSetLevel] = useMutation(InsertBinSetLevelMutation);
  const [updateBinSetLevel] = useMutation(UpdateBinSetLevelMutation);
  const [deleteBinSetLevel] = useMutation(DeleteBinSetLevelMutation);

  const {
    loading: loadingMarkerData,
    error: markerDataError,
    data: markerData,
    refetch: refetchMarkerData,
    startPolling: startMarkerDataPolling,
    stopPolling: stopMarkerDataPolling,
  } = useQuery(CALIBRATION_CARD_MARKER_DATA_QUERY, {
    variables: { bin_set_id: binSetID },
    skip: !binSetID,
  });

  const {
    loading: loadingBinSetCalibrations,
    data: binSetCalibrationData,
    refetch: refetchBinSetCalibrations,
  } = useQuery(CALIBRATION_CARD_GET_BIN_SET_CALIBRATIONS_QUERY, {
    variables: { bin_set_id: binSetID },
    skip: !binSetID,
  });

  const {
    loading: loadingCalibrationSeries,
    error: calibrationSeriesError,
    data: calibrationSeriesData,
    refetch: refetchCalibrationSeries,
  } = useQuery(CALIBRATION_CARD_CALIBRATION_SERIES_QUERY, {
    variables: { bin_set_id: binSetID },
    skip: !binSetID,
  });

  const binSet = markerData?.bin_set?.[0];
  const binSetCalibrations = binSetCalibrationData?.bin_set_calibration;
  const binSetLevels = binSet?.all_bin_set_levels;
  const earliestBinSetLevel = binSet?.earliest_bin_set_level?.[0];

  const isRecalibrating = binSetLevels?.some(
    (bsl) => bsl.calibration_status === 'recalculating' || bsl.calibration_status === 'calculating',
  );

  const refetchData = () => {
    refetchMarkerData();
    refetchBinSetCalibrations();
    refetchCalibrationSeries();
  };

  useEffect(() => {
    if (isRecalibrating) {
      startMarkerDataPolling(5000);
    } else {
      stopMarkerDataPolling();
      refetchData(); // refetch evverything once it's done recalibrating.
      // Yes. This is fetching the same data again for the marker data, but better call refetchData() than forget
      // to update a 2nd list of refetches.
    }

    return () => {
      stopMarkerDataPolling();
    };
  }, [isRecalibrating, startMarkerDataPolling, stopMarkerDataPolling]);

  useEffect(() => {
    if (earliestBinSetLevel?.valid_at) {
      setDateRange((prev) => ({
        ...prev,
        from: dayjs(earliestBinSetLevel.valid_at * 1000),
      }));
    }
  }, [earliestBinSetLevel?.valid_at]);

  const rowData = useMemo(
    () => ({
      barnName: binSet?.farm?.name || 'Unknown',
      bins: binSet?.bins?.map((b) => b.name).join(', ') || '',
    }),
    [binSet],
  );

  // Stitched cached calibrated series across all snapshots (sorted), used to anchor the live data tail.
  const cachedCalibratedDataPoints = useMemo(() => {
    const rows = calibrationSeriesData?.bin_set_calibration_series ?? [];
    return rows
      .flatMap((row) => row.calibrated_data_series ?? [])
      .slice()
      .sort((a, b) => a.t - b.t);
  }, [calibrationSeriesData]);

  // ConCal is infrequent, so fill the live data series past the latest calibrated point client-side.
  const { tailOriginalDataPoints, loading: loadingTail } = useCalibrationSeriesTail({
    binSetID,
    cachedCalibratedDataPoints,
    binSetLevels,
    now,
  });

  const chartSeries = useMemo(() => {
    const rows = calibrationSeriesData?.bin_set_calibration_series ?? [];
    const series = [];

    rows.forEach((row, index) => {
      const nextRow = rows[index + 1];
      const originalDataTrace = buildCachedSeriesTrace(
        stitchSeriesWithNext(row.original_data_series, nextRow?.original_data_series),
        COLOUR_PALETTE.originalDataColour,
        'Original Inventory Level',
        isMetric,
      );
      const calibratedDataTrace = buildCachedSeriesTrace(
        stitchSeriesWithNext(row.calibrated_data_series, nextRow?.calibrated_data_series),
        COLOUR_PALETTE.calibratedDataColour,
        'Calibrated Inventory Level',
        isMetric,
      );

      if (originalDataTrace) {
        series.push(originalDataTrace);
      }
      if (calibratedDataTrace) {
        series.push(calibratedDataTrace);
      }
    });

    // Client-computed live data tail, anchored to the last calibrated inventory point.
    const tailTrace = buildCachedSeriesTrace(
      tailOriginalDataPoints,
      COLOUR_PALETTE.liveDataColour,
      'Live Inventory Level',
      isMetric,
    );
    if (tailTrace) {
      series.push(tailTrace);
    }

    return series;
  }, [calibrationSeriesData, tailOriginalDataPoints, isMetric]);

  const binLevelResults = useMemo(() => {
    const rows = calibrationSeriesData?.bin_set_calibration_series ?? [];
    const latestRow = rows.length ? rows[rows.length - 1] : null;
    return (latestRow?.accuracy_details ?? [])
      .map((detail) => ({
        time: detail.valid_at,
        purpose: detail.purpose,
        target: detail.target_in_grams,
        liveValue: detail.live_value_in_grams,
        recalculatedValue: detail.recalculated_value_in_grams,
        totalFeedMeasured: detail.total_feed_measured_in_grams,
      }))
      .sort((a, b) => a.time - b.time);
  }, [calibrationSeriesData]);

  const chartLoading = loadingMarkerData || loadingBinSetCalibrations || loadingCalibrationSeries || loadingTail;
  const error = markerDataError || calibrationSeriesError;



  const onInsertBinSetLevel = (
    occurredAtInSeconds,
    levelInGrams,
    binSetId,
    source,
    method,
    purpose,
    expectedDeviationInGrams,
    notes,
    shouldRefetchChart,
  ) => {
    insertBinSetLevel({
      variables: {
        object: {
          valid_at: occurredAtInSeconds,
          level_in_grams: Math.round(levelInGrams),
          bin_set_id: binSetId,
          source,
          method,
          purpose,
          expected_deviation_in_grams: expectedDeviationInGrams,
          comment: notes,
        },
      },
      refetchQueries: shouldRefetchChart ? REFETCH_QUERIES : ['CalibrationCard_MarkerData'],
    });
  };

  const onUpdateBinSetLevel = (
    binSetLevelID,
    occurredAtInSeconds,
    levelInGrams,
    binSetId,
    source,
    method,
    purpose,
    expectedDeviationInGrams,
    notes,
  ) => {
    updateBinSetLevel({
      variables: {
        id: binSetLevelID,
        valid_at: occurredAtInSeconds,
        level_in_grams: Math.round(levelInGrams),
        bin_set_id: binSetId,
        source,
        method,
        purpose,
        expected_deviation_in_grams: expectedDeviationInGrams,
        comment: notes,
      },
      refetchQueries: ['CalibrationCard_MarkerData'],
    });
  };

  const onDeleteBinSetLevel = (binSetLevelID) => {
    deleteBinSetLevel({
      variables: {
        id: binSetLevelID,
        deleted_at: dayjs().unix(),
      },
      refetchQueries: REFETCH_QUERIES,
    });
  };

  let dataPaneChildren = null;
  const IdWrappedBinCheckId = withStaffOnlyIdWrapper(<span style={{ fontSize: '1rem' }}>{selectedBinSetLevelId}</span>);

  if (selectedBinSetLevelId || isCreatingNewBinSetLevel) {
    dataPaneChildren = (
      <>
        <h4>Bin Check</h4>
        {showStaffElements && selectedBinSetLevelId && <IdWrappedBinCheckId data={selectedBinSetLevelId} />}
        <hr />
        <BinCheckDataPane
          binSetLevelId={selectedBinSetLevelId}
          isEditing={!isCreatingNewBinSetLevel}
          binSetId={binSetID}
          numBins={binSet?.bins?.length}
          onClose={() => {
            setSelectedBinSetLevelId(null);
            setIsCreatingNewBinSetLevel(false);
          }}
        />
      </>
    );
  } else if (selectedBinSetCalibrationId || isCreatingNewBinSetCalibration) {
    dataPaneChildren = (
      <>
        <h4>Precision Calibration</h4>
        <hr />
        <BinSetCalibrationDataPane
          binSetCalibrationId={selectedBinSetCalibrationId}
          isEditing={!isCreatingNewBinSetCalibration}
          binSetId={binSetID}
          numBins={binSet?.bins?.length}
          onClose={() => {
            setSelectedBinSetCalibrationId(null);
            setIsCreatingNewBinSetCalibration(false);
          }}
        />
      </>
    );
  }

  return (
    <CalibrationCardView
      chartLoading={chartLoading}
      minDate={dayjs(earliestBinSetLevel?.valid_at * 1000)}
      maxDate={dayjs().add(1, 'day').endOf('day')}
      data={rowData}
      chartSeries={chartSeries}
      onInsertBinSetLevel={onInsertBinSetLevel}
      onUpdateBinSetLevel={onUpdateBinSetLevel}
      onDeleteBinSetLevel={onDeleteBinSetLevel}
      feedlines={feedlines}
      binSetLevels={binSetLevels}
      binSetCalibrations={binSetCalibrations}
      totalCapacityInGrams={rowData?.totalCapacity}
      deliveries={[]}
      dataPaneChildren={dataPaneChildren}
      onBinSetLevelClicked={(id) => {
        setIsCreatingNewBinSetLevel(false);
        if (id === selectedBinSetLevelId) {
          setSelectedBinSetLevelId(null);
        } else {
          setSelectedBinSetLevelId(id);
        }

        setSelectedBinSetCalibrationId(null);
      }}
      onBinSetCalibrationClicked={(id) => {
        if (id === selectedBinSetCalibrationId) {
          setSelectedBinSetCalibrationId(null);
        } else {
          setSelectedBinSetCalibrationId(id);
        }
        setSelectedBinSetLevelId(null);
      }}
      selectedBinSetLevel={selectedBinSetLevelId}
      selectedBinSetCalibration={selectedBinSetCalibrationId}
      onClickNewBinSetLevel={() => setIsCreatingNewBinSetLevel(true)}
      onClickNewBinSetCalibration={() => {
        setIsCreatingNewBinSetCalibration(true);
      }}
      dateRange={dateRange}
      setDateRange={setDateRange}
      error={error}
      displayCoefficientChart={user.isStaff && showCoefficientChart}
      binLevelResults={binLevelResults}
      displayBinLevelTable={user.isStaff && showBinLevelStoreTable}
      isRecalibrating={isRecalibrating}
      refetchData={refetchData}
      calibrationStyle={calibrationStyle}
    />
  );
}

CalibrationCard.propTypes = {
  binSetID: PropTypes.string,
  mode: PropTypes.string,
  feedlines: PropTypes.array,
  calibrationStyle: PropTypes.string,
};

export default CalibrationCard;
